import React, {useState} from "react";
import {useCollapsibleRows} from "@vigo/hooks";
import {Card, Table, Tooltip, Row} from "antd";

import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {Link} from "react-router-dom";
import {BackofficeFailedPaymentsFilters} from "./BackofficeFailedPaymentsFilters";
import {isConstantExist} from "../../../../utils/helpers";
import {codPaymentTypes} from "../../../../constants/constants";
import {useTranslation} from "react-i18next";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {ExpandableStyledTable} from "components/_defaultComponents/ExpandableStyledTable/ExpandableStyledTable.styles";
const PAGE_SIZE = 10;

export const BackofficeFailedPaymentsTab = ({isTabOpened = false}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const expandableState = useCollapsibleRows({isTabOpened, page});
    const [recall, setRecall] = useState(false);

    const [loading, codPaymentReferenceCodesResult] = useFetch({
        fetchFn: stores.codPaymentStore.getFailedPayments.bind(null, {
            ...filters,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE
        }),
        dependencies: [filters, page, isTabOpened, recall],
        initialCall: isTabOpened,
        condition: !stores.meStore.checkRole("isCompanyUser") && isTabOpened
    });

    const expandedRowRender = record => {
        const columns = [
            {
                title: "Ödeme Id",
                dataIndex: ["payment", "transactionId"],
                key: "transactionId",
                width: 100,
                ellipsis: {
                    showTitle: false
                },
                render: text => <Tooltip title={text}>{text}</Tooltip>,
                onCell: () => ({
                    "data-label": "Ödeme Id"
                })
            },
            {
                title: "Hesaba Yatırılacak Tutar",
                dataIndex: ["payment", "payableAmount"],
                key: "amount",
                width: 100,
                render: text => text && `${text} TL`,
                onCell: () => ({
                    "data-label": "Hesaba Yatırılacak Tutar"
                })
            },
            {
                title: "Fatura Tutarı",
                dataIndex: ["payment", "receivedAmount"],
                key: "invoiceAmount",
                width: 100,
                render: text => text && `${text} TL`,
                onCell: () => ({
                    "data-label": "Fatura Tutarı"
                })
            },
            {
                title: "İşlem Tarihi",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 150,
                render: text => new Date(text).toLocaleString("tr-TR"),
                onCell: () => ({
                    "data-label": "İşlem Tarihi"
                })
            },
            {
                title: "Mutabakat Tarihi",
                dataIndex: ["invoice", "reconciliationDate"],
                key: "reconciliationDate",
                width: 150,
                render: text => text && new Date(text).toLocaleDateString("tr-TR"),
                onCell: () => ({
                    "data-label": "Mutabakat Tarihi"
                })
            },

            {
                title: "Ödeme yöntemi",
                dataIndex: ["payment", "paymentType"],
                key: "paymentMethod",
                width: 120,
                render: paymentType =>
                    `${
                        isConstantExist(codPaymentTypes, paymentType)
                            ? t(`paymentTypes.${paymentType}`, {
                                  ns: "codPayment"
                              })
                            : paymentType
                    }`,
                onCell: () => ({
                    "data-label": "Ödeme yöntemi"
                })
            },
            {
                title: "Sipariş No",
                dataIndex: ["delivery", "vigoOrderNumber"],
                key: "vigoOrderNumber",
                width: 100,
                render: (text, row) => (
                    <Link
                        data-testid="vigoOrderNumber"
                        target="_blank"
                        to={{
                            pathname: "/orders/" + row?.deliveryInfo?._id
                        }}
                    >
                        {text}
                    </Link>
                ),
                onCell: () => ({
                    "data-label": "Sipariş No"
                })
            },
            {
                title: "Kurye",
                dataIndex: ["deliveryInfo", "assignedCourier", "fullname"],
                key: "courier",
                width: 150,
                render: (text, row) => (
                    <Link
                        data-testid="courierName"
                        target="_blank"
                        to={{
                            pathname: "/couriers/" + row?.deliveryInfo?.assignedCourier?._id
                        }}
                    >
                        {text}
                    </Link>
                ),
                onCell: () => ({
                    "data-label": "Kurye"
                })
            }
        ];

        return (
            <ResponsiveDataTable
                columns={columns}
                dataSource={record.codPayments}
                pagination={false}
                rowKey="_id"
                size="small"
            />
        );
    };

    const columns = [
        {
            title: "Referans Fatura No",
            dataIndex: "referenceCode",
            key: "referenceCode",
            onCell: () => ({
                "data-label": "Referans Fatura No"
            })
        },
        // Referans kodu tek bir alt firmaya ait olduğu için ilk ödemenin firma bilgisi kullanılıyor
        {
            title: "Firma",
            dataIndex: ["codPayments", 0, "deliveryInfo", "order", "pickupInfoCompanyName"],
            key: "company",
            render: (text, record) => (
                <Link
                    to={`/companies/${record?.codPayments?.[0]?.deliveryInfo?.order?.pickupInfoCompanyId}`}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Firma"
            })
        },
        {
            title: "Alt Firma",
            dataIndex: ["codPayments", 0, "deliveryInfo", "order", "pickupInfoName"],
            key: "subCompany",
            render: (text, record) => (
                <Link
                    to={`/companies/sub-company/${record?.codPayments?.[0]?.deliveryInfo?.order?.pickupInfoId}`}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Alt Firma"
            })
        },
        {
            title: "E-Fatura No",
            dataIndex: ["invoice", "documentId"],
            key: "invoiceDocumentId",
            onCell: () => ({
                "data-label": "E-Fatura No"
            })
        },
        {
            title: "E-Fatura Tarihi",
            dataIndex: ["invoice", "createdAt"],
            key: "invoiceCreatedAt",
            render: text => new Date(text).toLocaleDateString("tr-TR"),
            onCell: () => ({
                "data-label": "E-Fatura Tarihi"
            })
        },
        {
            title: "Geri Ödeme Tarihi",
            dataIndex: ["expectedPaymentDate"],
            key: "expectedPaymentDate",
            render: (expectedPaymentDate, row) =>
                (expectedPaymentDate &&
                    new Date(expectedPaymentDate).toLocaleDateString("tr-TR")) ||
                (row?.paymentDate && new Date(row?.paymentDate).toLocaleDateString("tr-TR")),
            onCell: () => ({
                "data-label": "Geri Ödeme Tarihi"
            })
        },
        {
            title: "Toplam Fatura Tutarı",
            dataIndex: "receivedAmount",
            key: "receivedAmount",
            render: text => text && `${text} TL`,
            onCell: () => ({
                "data-label": "Toplam Fatura Tutarı"
            })
        },
        {
            title: "Toplam İşlem Adedi",
            dataIndex: "codPayments",
            key: "codPayments",
            render: codPayments => codPayments?.length || 0,
            onCell: () => ({
                "data-label": "Toplam İşlem Adedi"
            })
        },
        {
            title: "Toplam Hesaba Yatırılacak Tutar",
            dataIndex: "payableAmount",
            key: "payableAmount",
            render: text => text && `${text} TL`,
            onCell: () => ({
                "data-label": "Toplam Hesaba Yatırılacak Tutar"
            })
        }
    ];

    return (
        <>
            <Card>
                <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                    <BackofficeFailedPaymentsFilters filters={filters} setFilters={setFilters} />
                </Row>
                <ExpandableStyledTable
                    loading={loading}
                    columns={columns}
                    size="small"
                    dataSource={codPaymentReferenceCodesResult?.rows || []}
                    rowKey="_id"
                    expandable={{
                        ...expandableState,
                        expandedRowRender
                    }}
                    pagination={{
                        total: codPaymentReferenceCodesResult?.dataCount || 0,
                        current: page,
                        hideOnSinglePage: true,
                        showSizeChanger: false,
                        pageSize: PAGE_SIZE,
                        onChange: page => {
                            setPage(page);
                        }
                    }}
                />
            </Card>
        </>
    );
};
